import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, money } from "@/lib/api";

const RANGES = ["1D", "1W", "1M"];

const label = (iso, range) =>
  new Date(iso).toLocaleString("en-US", range === "1D" ? { hour: "numeric" } : { month: "short", day: "numeric" });

export default function PriceChart({ symbol, endpoint, params = {}, fmt = money, testid }) {
  const [range, setRange] = useState("1W");
  const [points, setPoints] = useState([]);
  const id = (testid || symbol).replace(/[^A-Za-z0-9]/g, "");

  useEffect(() => {
    api.get(endpoint || `/market/${symbol}/history`, { params: { ...params, range } }).then((r) => setPoints(r.data.points));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, endpoint, params.pair, range]);

  const up = points.length > 1 && points[points.length - 1].price >= points[0].price;
  const color = up ? "#1a4fa0" : "#c8102e";
  const change = points.length > 1 ? ((points[points.length - 1].price - points[0].price) / points[0].price) * 100 : 0;

  return (
    <div className="border-t px-5 pt-4 pb-3" data-testid={`price-chart-${id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-semibold ${up ? "text-[var(--blue)]" : "text-[var(--red)]"}`} data-testid={`chart-change-${id}`}>
          {change >= 0 ? "+" : ""}{change.toFixed(2)}% over {range}
        </span>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setRange(r)} className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${r === range ? "bg-[var(--navy)] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`chart-range-${id}-${r}`}>{r}</button>
          ))}
        </div>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="time" tickFormatter={(v) => label(v, range)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} minTickGap={30} />
            <YAxis domain={["auto", "auto"]} hide />
            <Tooltip formatter={(v) => [fmt(v), "Price"]} labelFormatter={(v) => new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric" })} />
            <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill={`url(#g-${id})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
